import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

const NAV = [
  { to: '/',           icon: '⚡', label: 'Dashboard'   },
  { to: '/leads',      icon: '👥', label: 'Leads'        },
  { to: '/sequences',  icon: '🔁', label: 'Sequences'    },
  { to: '/pipeline',   icon: '📊', label: 'Pipeline'     },
  { to: '/aicalls',    icon: '📞', label: 'AI Calls'     },
  { to: '/whatsapp',   icon: '📱', label: 'WhatsApp'     },
  { to: '/bench',      icon: '🏪', label: 'Bench'        },
  { to: '/templates',  icon: '📋', label: 'Templates'    },
  { to: '/ai',         icon: '🤖', label: 'AI Assistant' },
  { to: '/settings',   icon: '⚙️', label: 'Settings'     },
];

const COMPANIES = ['Maven Group Global', 'B2techsoftLLC', 'Xolosoft LLC'];
const CO_COLORS = { 'Maven Group Global': '#00d4ff', 'B2techsoftLLC': '#7c3aed', 'Xolosoft LLC': '#00e676' };

// Global company + mode state (lifted to module scope for simplicity)
let _company = COMPANIES[0];
let _mode    = 'bdm';
let _subs    = [];
export const getCompany = () => _company;
export const getMode    = () => _mode;
export const subscribeGlobal = fn => { _subs.push(fn); return () => { _subs = _subs.filter(s => s !== fn); }; };
export const setGlobal = (company, mode) => {
  _company = company; _mode = mode;
  _subs.forEach(fn => fn({ company, mode }));
};

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [company, setCompany] = useState(COMPANIES[0]);
  const [mode,    setMode]    = useState('bdm');

  const handleCompany = c => { setCompany(c); setGlobal(c, mode); };
  const handleMode    = m => { setMode(m);    setGlobal(company, m); };

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <aside style={{
        width: 220, background: 'var(--surface)', borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '100vh',
      }}>
        {/* Logo */}
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 17, fontWeight: 700 }}>
            <span style={{ color: 'var(--accent)' }}>AUTO</span>PILOT
          </div>
          <div style={{ fontSize: 10, color: 'var(--muted)', letterSpacing: 1, marginTop: 2 }}>AI SALES ENGINE</div>
        </div>

        {/* Company selector */}
        <div style={{ padding: '14px 12px 10px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Company</div>
          {COMPANIES.map(c => (
            <button key={c} onClick={() => handleCompany(c)} style={{
              display: 'block', width: '100%', textAlign: 'left',
              background: company === c ? CO_COLORS[c] + '18' : 'transparent',
              border: `1px solid ${company === c ? CO_COLORS[c] + '55' : 'transparent'}`,
              borderRadius: 6, padding: '7px 10px', marginBottom: 4,
              color: company === c ? CO_COLORS[c] : 'var(--muted)',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: CO_COLORS[c], marginRight: 8 }} />
              {c}
            </button>
          ))}
        </div>

        {/* Mode toggle */}
        <div style={{ padding: '12px 12px 10px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Mode</div>
          {[['bdm','🚀 BDM / Projects'],['recruiting','👔 Recruiting']].map(([id, label]) => (
            <button key={id} onClick={() => handleMode(id)} style={{
              display: 'block', width: '100%', textAlign: 'left',
              background: mode === id ? 'var(--accent)18' : 'transparent',
              border: `1px solid ${mode === id ? 'var(--accent)44' : 'transparent'}`,
              borderRadius: 6, padding: '7px 10px', marginBottom: 4,
              color: mode === id ? 'var(--accent)' : 'var(--muted)',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>{label}</button>
          ))}
        </div>

        {/* Nav links */}
        <nav style={{ flex: 1, padding: '12px 10px' }}>
          {NAV.map(item => (
            <NavLink key={item.to} to={item.to} end={item.to === '/'} style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '9px 12px', borderRadius: 7, marginBottom: 2,
              textDecoration: 'none', fontSize: 13, fontWeight: 600,
              background:   isActive ? 'var(--accent)18' : 'transparent',
              color:        isActive ? 'var(--accent)' : 'var(--muted)',
              borderLeft:   isActive ? '3px solid var(--accent)' : '3px solid transparent',
            })}>
              <span>{item.icon}</span>{item.label}
            </NavLink>
          ))}
        </nav>

        {/* User info */}
        <div style={{ padding: '14px 16px', borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>{user?.name}</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 10 }}>{user?.email}</div>
          <button onClick={() => { logout(); nav('/login'); }} style={{
            background: 'var(--red)18', border: '1px solid var(--red)33',
            color: 'var(--red)', borderRadius: 6, padding: '6px 12px',
            fontSize: 12, fontWeight: 600, cursor: 'pointer', width: '100%',
          }}>Sign Out</button>
        </div>
      </aside>

      {/* Main content */}
      <main style={{ flex: 1, minHeight: '100vh', background: 'var(--bg)', overflowX: 'hidden' }}>
        <Outlet context={{ company, mode }} />
      </main>
    </div>
  );
}

// ── Reusable UI primitives ────────────────────────────────────────────────────
export const Btn = ({ children, onClick, primary, small, danger, disabled, style = {} }) => (
  <button onClick={onClick} disabled={disabled} style={{
    background: primary ? 'linear-gradient(135deg, var(--accent), #7c3aed)' : danger ? 'var(--red)18' : 'transparent',
    color:   primary ? '#000' : danger ? 'var(--red)' : 'var(--accent)',
    border:  primary ? 'none' : danger ? '1px solid var(--red)44' : '1px solid var(--accent)44',
    borderRadius: 6, padding: small ? '6px 14px' : '9px 20px',
    fontWeight: 700, fontSize: small ? 12 : 13, cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? .5 : 1, transition: 'all .15s',
    fontFamily: 'inherit', ...style,
  }}>{children}</button>
);

export const Badge = ({ label, color = 'var(--accent)' }) => (
  <span style={{
    background: color + '22', color, border: `1px solid ${color}44`,
    borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700,
    letterSpacing: .5, textTransform: 'uppercase',
  }}>{label}</span>
);

export const Card = ({ children, style = {} }) => (
  <div style={{
    background: 'var(--card)', border: '1px solid var(--border)',
    borderRadius: 10, padding: 20, ...style,
  }}>{children}</div>
);

export const PageHeader = ({ title, sub, children }) => (
  <div style={{ padding: '28px 28px 0', marginBottom: 24 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>{title}</h1>
        {sub && <p style={{ color: 'var(--muted)', fontSize: 14 }}>{sub}</p>}
      </div>
      {children && <div style={{ display: 'flex', gap: 10 }}>{children}</div>}
    </div>
  </div>
);

export const Input = ({ label, ...props }) => (
  <div style={{ marginBottom: 14 }}>
    {label && <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 5 }}>{label}</label>}
    <input {...props} style={{
      width: '100%', background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 7, padding: '10px 12px', color: 'var(--text)', fontSize: 13,
      outline: 'none', ...props.style,
    }} />
  </div>
);

export const Select = ({ label, children, ...props }) => (
  <div style={{ marginBottom: 14 }}>
    {label && <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 5 }}>{label}</label>}
    <select {...props} style={{
      width: '100%', background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 7, padding: '10px 12px', color: 'var(--text)', fontSize: 13,
      outline: 'none', cursor: 'pointer', ...props.style,
    }}>{children}</select>
  </div>
);

export const Modal = ({ title, onClose, children, width = 560 }) => (
  <div style={{
    position: 'fixed', inset: 0, background: '#000b', zIndex: 200,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  }} onClick={e => e.target === e.currentTarget && onClose()}>
    <div style={{
      background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
      padding: 28, width, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>{title}</div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>✕</button>
      </div>
      {children}
    </div>
  </div>
);
